/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Table } from 'antd';
import { congviecduanApi } from '@/services/action-plan/congviecduan';
import dayjs from 'dayjs';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';
import { CompareData, HistoryCompareModal, HistoryItem, safeParseJson } from './ModalHistory';
import { createUnitHelpers } from '@/components/MeasureIndicators/helpers/unitHelpers';
import { useAuthStore } from '@/store/useAuthStore';

export interface HistoryColumnItem {
  id: string;
  date: string;
  creator: string;
  position: string;
  person: string;
  content: React.ReactNode;
}

interface TableHistoryProps {
  idCongViec?: string | null;
  isDuAn?: boolean;
}

interface LichSuItem {
  id?: string;
  congViecID?: string;
  key?: string;
  ten?: string;
  noiDung?: string;
  noiDungMoi?: string;
  ngayTao?: string;
  nguoiTao?: number | string;
  nguoiCapNhat?: string | null;
  ngayCapNhat?: string | null;
  site?: string;
  totalCount?: number;
  rowNum?: number;
}

const TableHistory: React.FC<TableHistoryProps> = ({ idCongViec, isDuAn: isDuAnProp }) => {
  const [dataSource, setDataSource] = useState<HistoryColumnItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { ready, loadEmployee, loadDepartment, loadPosition, getEmployeeOrDepartmentFullName } = useLocalFilter();

  const [openCompare, setOpenCompare] = useState(false);
  const [compareOldData, setCompareOldData] = useState<CompareData>({});
  const [compareNewData, setCompareNewData] = useState<CompareData>({});
  const { profile } = useAuthStore();
  const tenantID = (profile as any)?.tenantID || '';
  const { units } = useLocalFilter();

  const unitHelpers = useMemo(() => createUnitHelpers(units, tenantID), [units, tenantID]);

  const handleOpenCompare = (lichSu: HistoryItem) => {
    setCompareOldData(safeParseJson(lichSu.noiDung));
    setCompareNewData(safeParseJson(lichSu.noiDungMoi));
    setOpenCompare(true);
  };

  useEffect(() => {
    if (idCongViec && ready) {
      void loadHistory();
    } else {
      setDataSource([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCongViec, ready]);

  const renderHistoryContent = (lichSu: LichSuItem, isDuAn: boolean): React.ReactNode => {
    if (lichSu.key === 'capnhatduan' || (lichSu.key === 'capnhatcongviec' && isDuAn)) {
      return (
        <>
          Cập nhật dự án:{' '}
          <Button
            size="small"
            type="link"
            className="!p-0"
            style={{ fontWeight: 'bold', fontSize: '12px' }}
            onClick={() => handleOpenCompare(lichSu as HistoryItem)}
          >
            Chi tiết
          </Button>
        </>
      );
    }

    if (lichSu.key === 'capnhatcongviec') {
      return (
        <>
          Cập nhật công việc:{' '}
          <Button
            size="small"
            type="link"
            className="!p-0"
            style={{ fontWeight: 'bold', fontSize: '12px' }}
            onClick={() => handleOpenCompare(lichSu as HistoryItem)}
          >
            Chi tiết
          </Button>
        </>
      );
    }

    return lichSu.ten ?? '';
  };

  const loadHistory = async () => {
    if (!idCongViec) return;

    try {
      setLoading(true);

      const response: any = await congviecduanApi.loadHistory(idCongViec);
      const apiData = response?.data ?? {};

      const list: LichSuItem[] = Array.isArray(apiData.list) ? apiData.list : [];
      const isDuAn = isDuAnProp ?? apiData.isDuAn ?? apiData.IsDuAn ?? false;

      const mapped: HistoryColumnItem[] = list.map((lichSu: LichSuItem) => {
        const nguoiTaoId = lichSu.nguoiTao != null ? String(lichSu.nguoiTao) : '';

        let creatorName = '';
        let positionText = '';

        if (nguoiTaoId) {
          const emp = loadEmployee(nguoiTaoId);
          creatorName = emp?.TenNhanVien ?? emp?.tenNhanVien ?? '';

          if (emp) {
            const dept = loadDepartment(String(emp.BPID ?? emp.bpid ?? ''));
            const pos = loadPosition(String(emp.CVID ?? emp.cvid ?? ''));
            positionText = [dept?.Ten ?? dept?.ten, pos?.Ten ?? pos?.ten]
              .filter(Boolean)
              .join(' / ');
          }
        }

        let personLabel = '';
        if (lichSu.noiDungMoi && isDuAn) {
          try {
            const json = JSON.parse(lichSu.noiDungMoi);
            const key = lichSu.key || '';

            if (key.includes('duan') && json.PhuTrachMucTieu != null) {
              personLabel = getEmployeeOrDepartmentFullName(String(json.PhuTrachMucTieu));
            } else if (json.NguoiPhuTrachID != null) {
              personLabel = getEmployeeOrDepartmentFullName(String(json.NguoiPhuTrachID));
            }
          } catch {
            // ignore parse error
          }
        }

        const content = renderHistoryContent(lichSu, isDuAn);

        return {
          id: lichSu.id ?? `${Date.now()}-${Math.random()}`,
          date: lichSu.ngayTao ? dayjs(lichSu.ngayTao).format('HH:mm DD/MM/YYYY') : '',
          creator: creatorName,
          position: positionText,
          person: personLabel,
          content,
        };
      });

      setDataSource(mapped);
    } catch (error) {
      console.error('Error loading history:', error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const boldCell = (val: string) =>
    val != null && String(val).trim() !== '' ? <span className="font-semibold">{val}</span> : null;

  const positionCell = (val: string) => {
    if (val == null || String(val).trim() === '') return null;

    const s = String(val).trim();
    const sep = ' / ';
    const i = s.indexOf(sep);

    if (i === -1) return <span className="font-semibold">{s}</span>;

    const boPhan = s.slice(0, i).trim();
    const chucVu = s.slice(i + sep.length).trim();

    return (
      <>
        <span className="font-semibold">{boPhan}</span>
        {chucVu ? <>{sep}{chucVu}</> : null}
      </>
    );
  };

  const tableColumns = [
    { title: 'Thời gian', dataIndex: 'date', key: 'date', align: 'left' as const, width: '15%' },
    {
      title: 'Người tạo',
      dataIndex: 'creator',
      key: 'creator',
      align: 'left' as const,
      width: '15%',
      render: (val: string) => boldCell(val),
    },
    {
      title: 'Chức vụ/ Bộ phận',
      dataIndex: 'position',
      key: 'position',
      align: 'left' as const,
      width: '15%',
      render: (val: string) => positionCell(val),
    },
    {
      title: 'Phòng ban/ Người phụ trách',
      dataIndex: 'person',
      key: 'person',
      align: 'center' as const,
      width: '15%',
      render: (val: string) => boldCell(val),
    },
    {
      title: <div className="text-center">Nội dung</div>,
      dataIndex: 'content',
      key: 'content',
      align: 'left' as const,
      width: '40%',
    },
  ];

  return (
    <>
      <div className="table-history-compact h-[calc(100vh-170px)] [&_.ant-table-thead>tr>th]:text-[12px] [&_.ant-table-thead>tr>th]:font-semibold [&_.ant-table-thead>tr>th]:bg-gray-50 [&_.ant-table-thead>tr>th]:text-gray-700 [&_.ant-table-thead>tr>th]:whitespace-normal [&_.ant-table-tbody_.ant-table-cell]:text-[12px] [&_.ant-table-cell]:py-1 [&_.ant-table-cell]:px-2 [&_.ant-table-cell]:whitespace-normal [&_.ant-table-cell]:break-words">
        <Table
          size="small"
          dataSource={dataSource}
          columns={tableColumns}
          tableLayout="fixed"
          rowKey="id"
          pagination={false}
          loading={loading}
          rowClassName={(_, index) =>
            index % 2 === 0 ? "bg-gray-50" : "bg-white"
          }
          components={{
            header: {
              wrapper: (props: any) => (
                <thead className="bg-gray-50 border-b border-[#e7eaf0] text-xs">
                  {props.children}
                </thead>
              ),
            },
            body: {
              wrapper: (props: any) => <tbody className="text-xs">{props.children}</tbody>,
            },
          }}
        />
      </div>

      <HistoryCompareModal
        open={openCompare}
        onClose={() => setOpenCompare(false)}
        oldData={compareOldData}
        newData={compareNewData}
        unitHelpers={unitHelpers}
        getEmployeeOrDepartmentFullName={getEmployeeOrDepartmentFullName}
      />
    </>
  );
};

export default TableHistory;